// modals/BulkCustomerInputModal.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Trash2 } from 'lucide-react';
import { getModalVariants, overlayVariants, buttonVariants } from '../utils/animationUtils';

const BulkCustomerInputModal = ({ show, onClose, onSave }) => {
    const [customers, setCustomers] = useState([{ name: '', phone: '' }]);

    if (!show) return null;

    const handleAddRow = () => {
        setCustomers([...customers, { name: '', phone: '' }]);
    };

    const handleRemoveRow = (index) => {
        if (customers.length > 1) {
            setCustomers(customers.filter((_, i) => i !== index));
        }
    };

    const handleChange = (index, field, value) => {
        const updated = [...customers];
        updated[index][field] = value;
        setCustomers(updated);
    };

    const handleSave = async () => {
        // 빈 행 제거 및 유효성 검사
        const validCustomers = customers
            .map(c => ({
                name: (c.name || '').trim(),
                phone: (c.phone || '').trim()
            }))
            .filter(c => c.name && c.phone);

        if (validCustomers.length === 0) {
            alert('최소 한 명의 고객 정보(이름, 연락처)를 입력해주세요.');
            return;
        }

        // 중복 연락처 체크
        const phones = validCustomers.map(c => c.phone);
        const duplicates = phones.filter((phone, index) => phones.indexOf(phone) !== index);
        if (duplicates.length > 0) {
            alert('중복된 연락처가 있습니다. 확인해주세요.');
            return;
        }

        try {
            await onSave(validCustomers);
            // 성공 후 초기화
            setCustomers([{ name: '', phone: '' }]);
            onClose();
        } catch (error) {
            console.error('고객 일괄 등록 실패:', error);
            alert('고객 등록 중 오류가 발생했습니다.');
        }
    };

    const handleClose = () => {
        setCustomers([{ name: '', phone: '' }]);
        onClose();
    };

    return (
        <AnimatePresence>
            <motion.div
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[1000]"
                variants={overlayVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                onClick={handleClose}
            >
                <motion.div
                    className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
                    variants={getModalVariants()}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* 헤더 */}
                    <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                        <h3 className="text-xl font-bold text-gray-800">고객 일괄 입력</h3>
                        <motion.button
                            onClick={handleClose}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            variants={buttonVariants}
                            whileHover="hover"
                            whileTap="tap"
                        >
                            <X className="w-5 h-5 text-gray-600" />
                        </motion.button>
                    </div>

                    {/* 내용 */}
                    <div className="p-6 overflow-y-auto flex-1">
                        <div className="mb-4 text-sm text-gray-600">
                            이름과 연락처를 입력하여 여러 고객을 한 번에 등록할 수 있습니다.
                        </div>

                        <div className="space-y-3">
                            {customers.map((customer, index) => (
                                <div key={index} className="flex gap-2 items-start">
                                    <div className="flex-1 grid grid-cols-2 gap-2">
                                        <input
                                            type="text"
                                            placeholder="이름"
                                            value={customer.name}
                                            onChange={(e) => handleChange(index, 'name', e.target.value)}
                                            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <input
                                            type="tel"
                                            placeholder="연락처"
                                            value={customer.phone}
                                            onChange={(e) => handleChange(index, 'phone', e.target.value)}
                                            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    {customers.length > 1 && (
                                        <motion.button
                                            onClick={() => handleRemoveRow(index)}
                                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                            variants={buttonVariants}
                                            whileHover="hover"
                                            whileTap="tap"
                                        >
                                            <Trash2 className="w-5 h-5" />
                                        </motion.button>
                                    )}
                                </div>
                            ))}
                        </div>

                        <motion.button
                            onClick={handleAddRow}
                            className="mt-4 w-full py-2 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-500 transition-colors flex items-center justify-center gap-2"
                            variants={buttonVariants}
                            whileHover="hover"
                            whileTap="tap"
                        >
                            <Plus className="w-5 h-5" />
                            행 추가
                        </motion.button>
                    </div>

                    {/* 푸터 */}
                    <div className="p-6 border-t border-gray-200 flex gap-3">
                        <motion.button
                            onClick={handleClose}
                            className="flex-1 py-3 px-4 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition-colors"
                            variants={buttonVariants}
                            whileHover="hover"
                            whileTap="tap"
                        >
                            취소
                        </motion.button>
                        <motion.button
                            onClick={handleSave}
                            className="flex-1 py-3 px-4 bg-blue-500 text-white rounded-xl font-bold hover:bg-blue-600 transition-colors"
                            variants={buttonVariants}
                            whileHover="hover"
                            whileTap="tap"
                        >
                            등록 ({customers.filter(c => c.name.trim() && c.phone.trim()).length}명)
                        </motion.button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default BulkCustomerInputModal;

